import { useMemo, useState } from "react"
import { useParams } from "react-router"
import { Alert, Box, Button, FormControlLabel, Link, Stack, Switch, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { isEnabled } from "lib/@components/feature"
import { WarningButton } from "lib/@components/colored-buttons/warning-button"
import { ListItemBox } from "lib/@components/ListItemBox"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useReferenceState } from "lib/@hooks/use-reference"
import { navigate } from "lib/routes/navigate"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import Iconify from "minimals-template/components/Iconify"
import { rateCards } from "routes/rate-cards/controller/rate-cards"
import { skillMap } from "routes/skill-mapper/controller/skill-map"
import { skillMapLines } from "routes/skill-mapper/controller/skill-map-lines"
import { SkillMapTradeRow } from "./skill-map-trade-row"
import { SkillMapActionsMenu } from "./skill-map-actions-menu"
import { restoreSkillMapWithFeedback } from "./restore-skill-map-with-feedback"
import { buildSkillMapTradeRows, buildUniqueSkillRows } from "./build-skill-map-trade-rows"
import { SkillMapViewEmpty } from "./skill-map-view-empty"
import { useEditMapping } from "./use-edit-mapping"
import { LinkedRateCardsBanner } from "./linked-rate-cards-banner"
import { TradeRowsWithHeader } from "./trade-rows-with-header"
import { GetStartedFab } from "routes/optimiser/get-started-fab"
import { useDemand } from "lib/authorization/use-demand"
import { FEATURE_FLAG_OPTIMISER_CONDITIONAL_TRADES } from "../../feature-flag-conditional-trades"
import { SkillMapSkillRoutingTop, SkillMapViewModes } from "./skill-map-view-slot-definitions"
import { SkillMapViewModeToggle } from "./skill-map-view-mode-toggle"

const parentCrumbs = [
    { label: "Optimiser", to: "/app/optimiser" },
    { label: "Skill Mappings", to: "/app/optimiser?optimiserTab=skill-mappings" },
]

const sortOptions = [
    { value: "alpha", label: "Alphabetically" },
    { value: "skills-desc", label: "Most Skills" },
    { value: "skills-asc", label: "Fewest Skills" },
]

export function SkillMapView() {
    const { id } = useParams()
    const map = skillMap.useResults({ id }, withInitialValue(IS_LOADING))
    const canManageOptimiser = useDemand("manageScenarios")
    const lines = skillMapLines.useResults({ skillMapId: id })
    const rcResult = rateCards.useResults({ skip: 0, take: 1000, status: "all" })
    const linkedRateCards = useMemo(getLinkedRateCards, [rcResult, id])
    const [search] = useSearchParam("search")
    const [getStarted] = useSearchParam("getStarted")
    const [sortMode, setSortMode] = useReferenceState("skillMapViewSort", "alpha")
    const [conditionalRoutingOnly, setConditionalRoutingOnly] = useState(false)
    const conditionalTradesOn = isEnabled(FEATURE_FLAG_OPTIMISER_CONDITIONAL_TRADES)
    // Views besides skill routing (e.g. FIQ-2457 schedule assignments) are plugged in
    const extraViews = SkillMapViewModes.useItems()
    const [viewMode, setViewMode] = useSearchParam("skillMapView", "skills")
    const activeView = extraViews.find(isChosenView)
    const viewOptions = [{ value: "skills", label: "Skill routing" }, ...extraViews.map(toViewOption)]
    const { openEditMapping, editMappingDrawer } = useEditMapping({
        skillMapId: id,
        allLines: lines,
        tradeVocabulary: map?.trades || {},
    })
    const tradeRows = useMemo(buildTradeRows, [
        map,
        lines,
        search,
        sortMode,
        conditionalRoutingOnly,
        conditionalTradesOn,
    ])

    if (map === IS_LOADING) return <LoadingScreen description="Loading Skill Map" />

    if (!map) {
        return (
            <PageBreadcrumbContainer title="Skill Map Not Found" parentCrumbs={parentCrumbs} sx={{ height: 1 }}>
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                        This skill map could not be found.
                    </Typography>
                </Stack>
            </PageBreadcrumbContainer>
        )
    }

    const { isArchived } = map

    return (
        <PageBreadcrumbContainer
            title={map.name}
            heading={
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
                    {/* drop inline-flex + add minWidth:0/ellipsis so a long unbroken name truncates within the header instead of overflowing the page */}
                    <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {map.name}
                    </Box>
                    <If value={canManageOptimiser}>
                        <SkillMapActionsMenu skillMapId={id} map={map} linkedRateCards={linkedRateCards} />
                    </If>
                </Stack>
            }
            description={map.description}
            parentCrumbs={parentCrumbs}
            sx={{ height: 1 }}
        >
            <Stack data-cy="skill-map-view" spacing={2} sx={{ px: 0, pt: 2, flex: 1 }}>
                <If value={isArchived}>
                    <Alert severity="warning" action={renderRestoreAction()}>
                        This skill map is archived and read-only. Restore it to make changes.
                    </Alert>
                </If>
                <If value={!isArchived && linkedRateCards.length === 1}>
                    <Alert severity="info" data-cy="linked-rate-cards-banner">
                        <Typography variant="body2">
                            This skill map is used by{" "}
                            <Link
                                component="button"
                                variant="body2"
                                onClick={navigateToFirstRateCard}
                                sx={{ verticalAlign: "baseline" }}
                            >
                                {linkedRateCards[0]?.name}
                            </Link>
                            . Changes to trades will be reflected there.
                        </Typography>
                    </Alert>
                </If>
                <If value={!isArchived && linkedRateCards.length > 1}>
                    <LinkedRateCardsBanner linkedRateCards={linkedRateCards} />
                </If>
                <Bound
                    skillMapId={id}
                    skillMapName={map.name}
                    trades={map.trades || {}}
                    lines={lines}
                    readOnly={isArchived || !canManageOptimiser}
                    onAddTrade={handleAddTrade}
                    onEditTrade={handleEditTrade}
                >
                    <If value={extraViews.length > 0}>
                        <SkillMapViewModeToggle
                            options={viewOptions}
                            value={activeView ? viewMode : "skills"}
                            onChange={setViewMode}
                        />
                    </If>
                    <If value={!!activeView}>
                        {activeView}
                        <Else>
                            <SkillMapSkillRoutingTop.Slot />
                            <ListItemBox spacing={1.5} sx={{ alignItems: "center" }}>
                                <If value={!isArchived && canManageOptimiser}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Iconify icon="mdi:plus" />}
                                        onClick={handleAddTrade}
                                        data-cy="skill-map-add-trade"
                                    >
                                        Add Trade
                                    </Button>
                                </If>
                                <Box flex={1} />
                                <If value={conditionalTradesOn}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={conditionalRoutingOnly}
                                                onChange={handleConditionalRoutingFilterChange}
                                                size="small"
                                                inputProps={{ "aria-label": "Show conditional routing only" }}
                                            />
                                        }
                                        label="Conditional routing only"
                                        data-cy="conditional-routing-filter"
                                        sx={{ m: 0, whiteSpace: "nowrap" }}
                                    />
                                </If>
                                <StandardSelector
                                    sortTitle="Sort:"
                                    options={sortOptions}
                                    value={sortMode}
                                    onChange={setSortMode}
                                    sx={{
                                        fontSize: "85%",
                                        color: "text.primary",
                                        whiteSpace: "nowrap",
                                        textTransform: "none",
                                        fontWeight: "normal",
                                    }}
                                />
                            </ListItemBox>
                            <SearchBoxUsingSearchParam autoFocus={false} placeholder="Search..." sx={{ py: 0, m: 0 }} />
                            <If value={tradeRows.length > 0}>
                                <RequestPageRepeat
                                    bind
                                    refreshKey={`${search ?? ""}:${conditionalRoutingOnly}`}
                                    total={tradeRows.length}
                                    list={tradeRows}
                                    pageSize={10}
                                    showTop
                                    ContentWrapper={TradeRowsWithHeader}
                                    item={
                                        <SkillMapTradeRow
                                            skillMapId={id}
                                            readOnly={isArchived || !canManageOptimiser}
                                            onEditMapping={openEditMapping}
                                        />
                                    }
                                />
                                <Else>
                                    <SkillMapViewEmpty
                                        hasLines={lines?.length > 0}
                                        hasSearch={!!search || conditionalRoutingOnly}
                                    />
                                </Else>
                            </If>
                        </Else>
                    </If>
                </Bound>
            </Stack>
            {editMappingDrawer}
            <GetStartedFab defaultOpen={getStarted === "true"} />
        </PageBreadcrumbContainer>
    )

    function handleAddTrade() {
        if (!canManageOptimiser) return
        openEditMapping()
    }

    // The same drawer the skill routing view opens, so trades can be renamed or given
    // skills without leaving the Schedule assignments view
    function handleEditTrade(tradeId) {
        if (!canManageOptimiser) return
        const skills = buildUniqueSkillRows(lines || []).filter(isForTrade)
        openEditMapping({ tradeId, tradeName: map.trades?.[tradeId] || tradeId, skills })

        function isForTrade(skill) {
            return skill.tradeId === tradeId
        }
    }

    function handleConditionalRoutingFilterChange(event) {
        setConditionalRoutingOnly(event.target.checked)
    }

    function renderRestoreAction() {
        if (!canManageOptimiser) return null
        return <WarningButton size="small" sx={{ marginRight: 1 }} text="Restore" onClick={handleRestore} />
    }

    function handleRestore() {
        return restoreSkillMapWithFeedback({ id, name: map.name })
    }

    function navigateToFirstRateCard() {
        if (linkedRateCards[0]) navigate(`/app/optimiser/rate-cards/${linkedRateCards[0].id}`)
    }

    function buildTradeRows() {
        return buildSkillMapTradeRows({
            map,
            lines,
            search,
            sortMode,
            conditionalRoutingOnly,
            conditionalTradesOn,
        })
    }

    function isChosenView(view) {
        return view.props.value === viewMode
    }

    function getLinkedRateCards() {
        return (rcResult?.items || [])
            .filter((rc) => rc.skillMapId === id && !rc.isArchived)
            .map((rc) => ({ id: rc.id, name: rc.name }))
    }
}

function toViewOption(view) {
    return { value: view.props.value, label: view.props.label }
}
